import { useState } from 'react';
import { CONFIRM_PHRASE, type FormState } from '../lib/validation';
import { appConfig } from '../config';

interface ConfirmationModalProps {
  open: boolean;
  form: FormState;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}

/**
 * High-risk confirmation modal. Requires the operator to type the exact phrase
 * "CONFIRM SETTLEMENT" before the final action is enabled.
 */
export function ConfirmationModal({ open, form, onCancel, onConfirm, submitting }: ConfirmationModalProps): JSX.Element | null {
  const [typed, setTyped] = useState('');
  if (!open) return null;

  const phraseMatches = typed === CONFIRM_PHRASE;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="w-full max-w-lg rounded-lg border border-red-500 bg-kore-card p-6">
        <h2 id="confirm-title" className="text-lg font-semibold text-red-400">
          Confirm Production Bank Settlement Action
        </h2>
        <p className="mt-3 text-sm text-slate-300">
          You are about to force a real production resettle and resync for this KoreID, submit a
          PACS.028 nudge where supported, capture bank acknowledgement, and run payment
          confirmation reconciliation. This action must only be performed on authorised bank
          request.
        </p>

        <dl className="mt-4 space-y-1 rounded border border-slate-700 bg-slate-900/50 p-3 text-sm">
          <div className="flex justify-between gap-4"><dt className="text-slate-400">KoreID</dt><dd className="font-medium text-white">{form.koreId}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Bank Request Reference</dt><dd className="font-medium text-white">{form.bankRequestReference}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Bank Name</dt><dd className="font-medium text-white">{form.bankName}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Settlement Rail</dt><dd className="font-medium text-emerald-300">{form.settlementRail} — LIVE</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-slate-400">Authenticated User</dt><dd className="font-medium text-white">{appConfig.allowedUser}</dd></div>
          {form.sovereignOverride && (
            <div className="flex justify-between gap-4"><dt className="text-amber-400">Sovereign Override</dt><dd className="font-medium text-amber-300">Omega777 (audited, non-bypassing)</dd></div>
          )}
        </dl>

        <label htmlFor="confirm-phrase" className="mt-4 block text-sm text-slate-300">
          Type <span className="font-mono font-semibold text-white">{CONFIRM_PHRASE}</span> to enable execution:
        </label>
        <input
          id="confirm-phrase"
          type="text"
          autoComplete="off"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500"
          aria-invalid={!phraseMatches}
        />

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onCancel} disabled={submitting} className="rounded border border-slate-500 bg-slate-800 px-4 py-2 text-sm hover:bg-slate-700 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!phraseMatches || submitting}
            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? 'Executing…' : 'Execute Production Settlement'}
          </button>
        </div>
      </div>
    </div>
  );
}
